import { useState, useMemo } from 'react'
import { Search, FileText } from 'lucide-react'
import { Dialog, DialogHeader } from '@/components/ui/Dialog'
import { useMemoLinkStore } from '@/stores/memoLinkStore'
import type { LinkedMemoRef } from '@/lib/types'

interface MemoLinkModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (memo: LinkedMemoRef) => void
  excludeIds?: string[]
}

export function MemoLinkModal({ isOpen, onClose, onSelect, excludeIds = [] }: MemoLinkModalProps) {
  const { memos, isConnected, isLoading, search } = useMemoLinkStore()
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const list = query.trim() ? search(query) : memos
    return list.filter((m) => !excludeIds.includes(m.syncId))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memos, query, excludeIds])

  const handleSelect = (memo: typeof memos[0]) => {
    onSelect({
      memoSyncId: memo.syncId,
      memoTitle: memo.title,
      memoPreview: memo.body?.slice(0, 100),
      memoColor: memo.color || '#94a3b8',
      linkedAt: new Date().toISOString(),
      tags: memo.tags,
    })
    setQuery('')
    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogHeader title="메모 연결" onClose={onClose} />

      {!isConnected ? (
        <div className="py-12 text-center">
          <FileText className="w-10 h-10 mx-auto text-disabled mb-3" />
          <p className="text-sm text-caption">Memo 앱에 연결되지 않았습니다.</p>
          <p className="text-xs text-disabled mt-1">Google 로그인 후 사용 가능합니다.</p>
        </div>
      ) : (
        <>
          {/* 검색 */}
          <div className="px-4 py-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-caption" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="메모 검색..."
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-base bg-surface-secondary text-heading focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* 목록 */}
          <div className="px-4 pb-4 max-h-[50dvh] overflow-y-auto space-y-2">
            {isLoading && (
              <p className="text-center text-sm text-caption py-8">불러오는 중...</p>
            )}
            {!isLoading && filtered.length === 0 && (
              <p className="text-center text-sm text-caption py-8">
                {query ? '검색 결과가 없습니다' : '메모가 없습니다'}
              </p>
            )}
            {filtered.map((memo) => (
              <button
                key={memo.syncId}
                onClick={() => handleSelect(memo)}
                className="w-full text-left p-3 rounded-xl border border-base hover-surface transition-colors border-l-4"
                style={{ borderLeftColor: memo.color || '#94a3b8' }}
              >
                <p className="text-sm font-medium text-heading truncate">{memo.title}</p>
                {memo.tags?.length > 0 && (
                  <div className="flex gap-1 mt-0.5">
                    {memo.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="text-meta text-primary-500">#{tag}</span>
                    ))}
                  </div>
                )}
                {memo.body && (
                  <p className="text-xs text-caption mt-1 line-clamp-2">{memo.body.slice(0, 80)}</p>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </Dialog>
  )
}
